import React from "react";
import { Row, Col, Button } from "reactstrap";
import { Icon } from "@material-ui/core";
import InputFeild from "../../../global/form/input";
import SelectList from "../../../global/form/select";
import { FieldLabel, TagListInput, ColumnInput, IconButton, useKeyedEntries } from "./common_controls";
import {
  OPERATORS,
  getOperatorMeta,
  TIME_UNITS,
  ARITHMETIC_OPS,
  VALUE_COMPARE_OPS,
  UPDATE_TYPES,
  UPDATE_DB_OPTIONS,
  FORM_RESPONSE_FIELDS,
  CUSTOMER_SUB_TABLES
} from "./config_model";

const ConditionValueEditor = ({ cond, onChange, knownColumns }) => {
  const meta = getOperatorMeta(cond.op) || { valueType: "text" };
  switch (meta.valueType) {
    case "tags":
      return <TagListInput values={cond.val || []} onChange={val => onChange({ val })} placeholder="Add value" />;
    case "number":
      return <InputFeild type="number" placeholder="Value" value={cond.val ?? ""} onChange={e => onChange({ val: Number(e.target.value) })} />;
    case "datetime":
      return <InputFeild placeholder="YYYY-MM-DD or YYYY-MM-DD HH:mm:ss" value={cond.val || ""} onChange={e => onChange({ val: e.target.value })} />;
    case "daterange":
      return (
        <Row noGutters>
          <Col className="pr-1"><InputFeild placeholder="From" value={(cond.val && cond.val[0]) || ""} onChange={e => onChange({ val: [e.target.value, (cond.val && cond.val[1]) || ""] })} /></Col>
          <Col className="pl-1"><InputFeild placeholder="To" value={(cond.val && cond.val[1]) || ""} onChange={e => onChange({ val: [(cond.val && cond.val[0]) || "", e.target.value] })} /></Col>
        </Row>
      );
    case "timediff":
      return (
        <Row noGutters>
          <Col xs={5} className="pr-1">
            <ColumnInput value={cond.col2} onChange={col2 => onChange({ col2 })} placeholder="Compare with column" suggestions={knownColumns} />
          </Col>
          <Col xs={3} className="px-1">
            <SelectList value={cond.unit || "hours"} onChange={e => onChange({ unit: e.target.value })}>
              {TIME_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </SelectList>
          </Col>
          <Col xs={4} className="pl-1">
            <InputFeild type="number" placeholder="Value" value={cond.val ?? ""} onChange={e => onChange({ val: Number(e.target.value) })} />
          </Col>
        </Row>
      );
    case "arithmetic":
      return (
        <>
          <TagListInput values={cond.cols || []} onChange={cols => onChange({ cols })} placeholder="Other columns to combine" suggestions={knownColumns} />
          <Row noGutters className="mt-1">
            <Col xs={4} className="pr-1">
              <SelectList value={cond.cols_op || "ADD"} onChange={e => onChange({ cols_op: e.target.value })}>
                {ARITHMETIC_OPS.map(o => <option key={o} value={o}>{o}</option>)}
              </SelectList>
            </Col>
            <Col xs={4} className="px-1">
              <SelectList value={cond.val_op || "GTE"} onChange={e => onChange({ val_op: e.target.value })}>
                {VALUE_COMPARE_OPS.map(o => <option key={o} value={o}>{o}</option>)}
              </SelectList>
            </Col>
            <Col xs={4} className="pl-1">
              <InputFeild type="number" placeholder="Value" value={cond.val ?? ""} onChange={e => onChange({ val: Number(e.target.value) })} />
            </Col>
          </Row>
        </>
      );
    default:
      return <InputFeild placeholder="Pattern" value={cond.val || ""} onChange={e => onChange({ val: e.target.value })} />;
  }
};

const ConditionRow = ({ colKey, cond, onChangeKey, onChangeCond, onRemove, columnMode, suggestions }) => {
  const meta = getOperatorMeta(cond.op);
  const showDateFormat = meta && ["datetime", "daterange", "timediff"].includes(meta.valueType);
  return (
    <div className="border shadow-sm mx-0 my-2 p-2">
      <Row className="align-items-center">
        <Col xs={4}>
          {columnMode === "input_id" ? (
            <InputFeild type="number" placeholder="Input ID" value={colKey} onChange={e => onChangeKey(e.target.value)} />
          ) : (
            <ColumnInput value={colKey} onChange={onChangeKey} placeholder="Column name" suggestions={suggestions} />
          )}
        </Col>
        <Col xs={5}>
          <SelectList value={cond.op || ""} onChange={e => onChangeCond({ op: e.target.value })}>
            {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </SelectList>
        </Col>
        <Col xs="auto" className="ml-auto">
          <IconButton icon="delete" danger onClick={onRemove} title="Remove condition" />
        </Col>
      </Row>
      {cond.op && (
        <Row className="mt-2">
          <Col>
            <ConditionValueEditor cond={cond} onChange={patch => onChangeCond(patch)} knownColumns={suggestions} />
          </Col>
        </Row>
      )}
      {showDateFormat && (
        <Row className="mt-1">
          <Col>
            <InputFeild placeholder="Date format in report, optional e.g. DD-MM-YYYY" value={cond.input_date_format || ""} onChange={e => onChangeCond({ input_date_format: e.target.value })} />
          </Col>
        </Row>
      )}
    </div>
  );
};

export const ConditionGroup = ({ title, helper, conditions, onChange, columnMode, suggestions }) => {
  const { rows, setKey, setData, addRow, removeRow } = useKeyedEntries(conditions, onChange);

  return (
    <FieldLabel label={title} helper={helper}>
      {rows.map(row => (
        <ConditionRow
          key={row.id}
          colKey={row.key}
          cond={row.data}
          onChangeKey={val => setKey(row.id, val)}
          onChangeCond={patch => setData(row.id, { ...row.data, ...patch })}
          onRemove={() => removeRow(row.id)}
          columnMode={columnMode}
          suggestions={suggestions}
        />
      ))}
      <Button size="sm" color="link" className="p-0" onClick={() => addRow("", { op: "INCLUDES", val: [] })}><Icon fontSize="small" className="align-middle">add</Icon> Add condition</Button>
    </FieldLabel>
  );
};

const ActionRow = ({ actionKey, action, ruleDb, onChangeKey, onChangeAction, onRemove, suggestions }) => {
  const subTable = CUSTOMER_SUB_TABLES.find(t => t.value === Number(action.db)) || CUSTOMER_SUB_TABLES[0];
  const keySuggestions = ruleDb === "customer_leads" ? subTable.fields : ruleDb === "form_response_data" ? [] : FORM_RESPONSE_FIELDS;

  return (
    <div className="border shadow-sm mx-0 my-2 p-2">
      <Row className="align-items-center">
        <Col xs={ruleDb === "customer_leads" ? 3 : 4}>
          {ruleDb === "form_response_data" ? (
            <InputFeild type="number" placeholder="Input ID" value={actionKey} onChange={e => onChangeKey(e.target.value)} />
          ) : (
            // These field lists are fixed (not arbitrary report columns), so pick from a dropdown
            // rather than free text — but keep an already-saved, non-standard key selectable too
            // instead of silently hiding it.
            <SelectList value={actionKey || ""} placeholder="Field to update" onChange={e => onChangeKey(e.target.value)}>
              {(actionKey && !keySuggestions.includes(actionKey) ? [actionKey, ...keySuggestions] : keySuggestions).map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </SelectList>
          )}
        </Col>
        {ruleDb === "customer_leads" && (
          <Col xs={3}>
            <SelectList value={Number(action.db) || 1} onChange={e => onChangeAction({ db: Number(e.target.value) })}>
              {CUSTOMER_SUB_TABLES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </SelectList>
          </Col>
        )}
        <Col xs={ruleDb === "customer_leads" ? 4 : 5}>
          <SelectList value={action.type ?? 0} onChange={e => onChangeAction({ type: Number(e.target.value) })}>
            {UPDATE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </SelectList>
        </Col>
        <Col xs="auto" className="ml-auto">
          <IconButton icon="delete" danger onClick={onRemove} title="Remove update" />
        </Col>
      </Row>
      <Row className="mt-2">
        <Col>
          {Number(action.type) === 0 && <InputFeild placeholder="Value to set" value={action.val ?? ""} onChange={e => onChangeAction({ val: e.target.value })} />}
          {Number(action.type) === 1 && <TagListInput values={action.val || []} onChange={val => onChangeAction({ val })} placeholder="Report column to copy" suggestions={suggestions} />}
          {(Number(action.type) === 2 || Number(action.type) === 3) && (
            <>
              <InputFeild placeholder={'Template e.g. "{{colA}}-{{colB}}"'} value={action.val || ""} onChange={e => onChangeAction({ val: e.target.value })} />
              <div className="small text-muted mt-1 mb-1">Template columns</div>
              <TagListInput values={action.cols || []} onChange={cols => onChangeAction({ cols })} placeholder="Column used in template" suggestions={suggestions} />
            </>
          )}
          {Number(action.type) === 3 && (
            <MappingTableEditor mapping={action.mapping || {}} onChange={mapping => onChangeAction({ mapping })} />
          )}
        </Col>
      </Row>
      <Row className="mt-1">
        <Col>
          <div className="small text-muted mb-1">Only apply if current value is (optional)</div>
          <TagListInput values={action.previousValues || []} onChange={previousValues => onChangeAction({ previousValues })} placeholder="Existing value" />
        </Col>
      </Row>
    </div>
  );
};

const MappingTableEditor = ({ mapping, onChange }) => {
  const { rows, setKey, setData, addRow, removeRow } = useKeyedEntries(mapping, onChange);
  return (
    <div className="mt-2">
      <div className="small text-muted mb-1">Value mapping (report value → friendly label)</div>
      {rows.map(row => (
        <Row key={row.id} noGutters className="mb-1">
          <Col xs={5} className="pr-1"><InputFeild placeholder="Report value" value={row.key} onChange={e => setKey(row.id, e.target.value)} /></Col>
          <Col xs={5} className="px-1"><InputFeild placeholder="Label" value={row.data} onChange={e => setData(row.id, e.target.value)} /></Col>
          <Col xs="auto"><IconButton icon="delete" danger onClick={() => removeRow(row.id)} /></Col>
        </Row>
      ))}
      <Button size="sm" color="link" className="p-0" onClick={() => addRow("", "")}><Icon fontSize="small" className="align-middle">add</Icon> Add mapping</Button>
    </div>
  );
};

export const ActionList = ({ update, ruleDb, onChange, suggestions }) => {
  const { rows, setKey, setData, addRow, removeRow } = useKeyedEntries(update, onChange);

  return (
    <FieldLabel label="Then update" helper="What should change when the conditions above are met.">
      {rows.map(row => (
        <ActionRow
          key={row.id}
          actionKey={row.key}
          action={row.data}
          ruleDb={ruleDb}
          onChangeKey={val => setKey(row.id, val)}
          onChangeAction={patch => setData(row.id, { ...row.data, ...patch })}
          onRemove={() => removeRow(row.id)}
          suggestions={suggestions}
        />
      ))}
      <Button size="sm" color="link" className="p-0" onClick={() => addRow("", { type: 0, val: "" })}><Icon fontSize="small" className="align-middle">add</Icon> Add update</Button>
    </FieldLabel>
  );
};

const ConditionRuleBuilder = ({ data, onChange, knownColumns }) => {
  const setData = patch => onChange({ ...data, ...patch });

  return (
    <div>
      <FieldLabel label="Applies to" helper="What kind of record this rule updates.">
        <SelectList value={data.db || ""} onChange={e => setData({ db: e.target.value || undefined })}>
          {UPDATE_DB_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </SelectList>
      </FieldLabel>

      <ConditionGroup title="If the report row matches" conditions={data.where} onChange={where => setData({ where })} suggestions={knownColumns} />
      <ConditionGroup title="And the existing task matches (optional)" conditions={data.db_where} onChange={db_where => setData({ db_where })} suggestions={FORM_RESPONSE_FIELDS} />
      <ConditionGroup title="And an existing input value matches (optional)" conditions={data.input_where} onChange={input_where => setData({ input_where })} columnMode="input_id" />

      <ActionList update={data.update} ruleDb={data.db} onChange={update => setData({ update })} suggestions={knownColumns} />
    </div>
  );
};

export default ConditionRuleBuilder;
